import { Link as NextLink } from "@/i18n/routing";
import { useParams } from "next/navigation";
import type { ComponentProps } from "react";

type NextLinkProps = ComponentProps<typeof NextLink>;
type Href = NextLinkProps["href"];

interface LinkProps extends NextLinkProps {
  ignoreStore?: boolean;
  preserveQuery?: boolean;
}

export const Link = ({
  href,
  ignoreStore = false,
  preserveQuery = true,
  ...props
}: LinkProps) => {
  const params = useParams();
  const store = params.store as string;

  const ensureStorePath = (path: string): string => {
    if (ignoreStore) return path;

    // Handle query parameters
    const [basePath, query] = path.split("?");
    const queryString = preserveQuery && query ? `?${query}` : "";

    // Special handling for root path
    if (basePath === "/" || basePath === "") {
      return `/${store}${queryString}`;
    }

    // Remove leading slash if present
    const cleanPath = basePath.startsWith("/") ? basePath.slice(1) : basePath;

    if (cleanPath.startsWith(`${store}/`)) {
      return `/${cleanPath}${queryString}`;
    }

    return `/${store}/${cleanPath}${queryString}`;
  };

  const processHref = (href: Href) => {
    if (typeof href === "string") {
      return ensureStorePath(href);
    }

    const pathname =
      href.pathname === "/"
        ? `/${store}`
        : ensureStorePath(href.pathname ?? "");

    return {
      ...href,
      pathname,
    };
  };

  if (!store) {
    return null;
  }

  const processedHref = processHref(href);

  return <NextLink href={processedHref} {...props} />;
};
